import * as React from 'react';
import * as _ from 'underscore';

const CANCEL_TIMEOUT = 0;

const _callbacks: {
    [i: number]: {
        priority: number;
        callback;
    };
} = {};

let _index = 0;
let _cancelFlag: number | boolean = null;

/**
 * Registers a callback function with a specified priority.
 *
 * @param callback - The function to be called when the event is triggered.
 * @param priority - The priority of the callback. Higher priority callbacks are executed first. Defaults to 0.
 * @returns The index of the registered callback.
 */
export function register(callback, priority: number = 0) {
    _index += 1;

    _callbacks[_index] = {
        priority: priority,
        callback: callback
    };

    return _index;
}

/**
 * Unregisters a callback function by its identifier.
 *
 * @param i - The identifier of the callback function to unregister.
 */
export function unregister(i) {
    delete _callbacks[i];
}

/**
 * Cancels an ongoing operation based on the provided priority.
 *
 * If no priority is provided, it sets the cancel flag to `true`.
 * If a priority is provided, it sets the cancel flag to the given priority.
 *
 * The cancel flag is reset to `null` after a timeout defined by `CANCEL_TIMEOUT`.
 *
 * @param {number} [priority] - The priority level for the cancellation.
 */
export function cancel(priority?: number) {
    if (_cancelFlag != null) {
        return;
    }

    if (priority == null) {
        _cancelFlag = true;
    } else {
        _cancelFlag = priority;
    }

    setTimeout(() => {
        _cancelFlag = null;
    }, CANCEL_TIMEOUT);
}

/**
 * Triggers the registered callbacks based on the event and priority.
 *
 * @param {Event} e - The event object that will be passed to the callbacks.
 * @param {number} [priority] - Optional priority level to determine which callbacks should be executed.
 * If provided, only callbacks with a priority equal to or higher than this value will be executed.
 *
 * The function iterates over the registered callbacks and executes them if they meet the priority criteria.
 * If a callback's priority is lower than the specified priority, it will be skipped.
 * After executing the callbacks, the cancelFlag is reset to null.
 */
export function trigger(e, priority?: number) {
    if (_cancelFlag === true) {
        _cancelFlag = null;
        return;
    }

    if (priority != null) {
        _cancelFlag = priority;
    }

    _.each(_.keys(_callbacks), (key) => {
        const obj = _callbacks[key];

        if (typeof _cancelFlag === 'number' && obj.priority < _cancelFlag) {
            return;
        }

        if (typeof obj.callback !== 'function') {
            return;
        }

        obj.callback(e);
    });

    _cancelFlag = null;
}

let _doNotProcess = false;

export function setDoNotProcessFlag(value: boolean) {
    _doNotProcess = value;
}

export function useGlobalClickHook(onClick: () => void, priority?: number) {
    const globalClickIndex = React.useRef(-1);

    React.useEffect(() => {
        globalClickIndex.current = register((e) => {
            if (onClick != null && !_doNotProcess) {
                onClick();
            }
        }, priority);

        return () => {
            unregister(globalClickIndex.current);
            globalClickIndex.current = null;
        };
    }, [onClick, priority]);
}
